declare global {
  interface Window {
    // `dataLayer` is required to match the canonical declaration in
    // @tastyworks/shared-platform-core (svelte-spa-router.d.ts), which is the
    // one present in consuming apps' typecheck graphs.
    dataLayer: any[]
    gtmBuffer?: any[]
    gtmLoaded?: boolean
    /**
     * Guards against re-injecting the GTM script. Lives on `window`, which is
     * shared across single-spa micro-apps, so repeated init calls inject once.
     */
    gtmScriptInjected?: boolean
  }
}

/**
 * Push onto the GTM dataLayer. Each argument becomes its own dataLayer entry,
 * so callers pass a single object for an event, or a single array for a GTM
 * command (e.g. `gtag(["consent", "default", {...}])`). Mirrors GTM's own
 * `gtag` shape; lifted verbatim from the app-side helper.
 */
export function gtag(...args: any[]) {
  ;(window.dataLayer ??= []).push(...args)
}

/**
 * Push onto the dataLayer once GTM has loaded; otherwise queue until
 * flushGtmBuffer runs. Lets callers fire events before the script is ready.
 */
export function bufferedGtag(...args: any[]) {
  if (window.gtmLoaded) {
    gtag(...args)
  } else {
    ;(window.gtmBuffer ??= []).push(...args)
  }
}

/** Flush queued events onto the dataLayer and mark GTM as loaded. */
export function flushGtmBuffer() {
  window.gtmLoaded = true
  ;(window.dataLayer ??= []).push(...(window.gtmBuffer ?? []))
}

/**
 * Inject the Google Tag Manager script for the given container. No-ops when
 * containerId is empty (analytics disabled for the environment). Idempotent:
 * repeated calls (multiple micro-apps on one window, or repeated consent
 * changes) inject the script and push the gtm.start marker only once.
 */
export function loadGoogleTagManager(
  containerId: string,
  { dataLayerName = "dataLayer" }: { dataLayerName?: string } = {}
) {
  if (!containerId) {
    return
  }
  if (window.gtmScriptInjected) {
    return
  }
  window.gtmScriptInjected = true

  const layer = ((window as any)[dataLayerName] ??= [])
  layer.push({ "gtm.start": Date.now(), event: "gtm.js" })

  const firstScript = document.getElementsByTagName("script")[0]
  const script = document.createElement("script")
  script.async = true
  const l = dataLayerName !== "dataLayer" ? "&l=" + dataLayerName : ""
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}${l}`
  firstScript.parentNode?.insertBefore(script, firstScript)
}
